import React, { useState, useEffect, useRef } from 'react';
import { useResponsive } from '../../hooks/useResponsive';

interface SelfReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (stats: { goals: number; assists: number; saves: number }) => void;
  isLoading?: boolean;
  existingStats?: { goals: number; assists: number; saves: number };
  isValidationPending?: boolean;
  validationStatus?: 'PENDING' | 'APPROVED' | 'REJECTED';
  validatorName?: string;
  rejectionInfo?: { reason: string; rejectedBy: string } | null;
}

export const SelfReportModal: React.FC<SelfReportModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  existingStats = { goals: 0, assists: 0, saves: 0 },
  isValidationPending = false,
  validationStatus = 'PENDING',
  validatorName,
  rejectionInfo
}) => {
  const { isMobile } = useResponsive();
  const [goals, setGoals] = useState(existingStats.goals);
  const [assists, setAssists] = useState(existingStats.assists);
  const [saves, setSaves] = useState(existingStats.saves);
  const [showReportForm, setShowReportForm] = useState(false);
  const rejectionReasonRef = useRef<HTMLDivElement>(null);

  // Actualizar estado cuando cambien los datos existentes
  useEffect(() => {
    setGoals(existingStats.goals);
    setAssists(existingStats.assists);
    setSaves(existingStats.saves);
  }, [existingStats]);

  // Auto-focus en el motivo del rechazo cuando se abre el modal con rejectionInfo
  useEffect(() => {
    if (isOpen && rejectionInfo && rejectionReasonRef.current) {
      setTimeout(() => {
        rejectionReasonRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }, 100);
    }
  }, [isOpen, rejectionInfo]);

  // Resetear estado cuando se cierra el modal
  useEffect(() => {
    if (!isOpen) {
      setShowReportForm(false);
    }
  }, [isOpen]);

  const handleSubmit = () => {
    onSubmit({ goals, assists, saves });
  };

  const handleShowReportForm = () => {
    setShowReportForm(true);
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '10px' : '20px'
    }}>
      <div style={{
        backgroundColor: '#23272A',
        borderRadius: isMobile ? '12px' : '16px',
        padding: isMobile ? '16px' : '24px',
        maxWidth: isMobile ? 'calc(100vw - 20px)' : '500px',
        width: '100%',
        maxHeight: isMobile ? 'calc(100vh - 20px)' : '80vh',
        border: '1px solid #33383B',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
        overflowY: 'auto'
      }}>
        <div style={{ marginBottom: '20px', textAlign: 'center' }}>
          <h2 style={{ 
            color: '#FFFFFF', 
            margin: '0 0 8px 0', 
            fontSize: isMobile ? '18px' : '20px',
            fontWeight: 'bold'
          }}>
            📊 Reportar Mis Estadísticas
          </h2>
          <p style={{ 
            color: '#B0BEC5', 
            margin: 0, 
            fontSize: isMobile ? '13px' : '14px',
            lineHeight: '1.4'
          }}>
            {isValidationPending 
              ? `Tu reporte está siendo revisado por ${validatorName || 'un compañero de equipo'}`
              : 'Completa tus estadísticas del partido. Un compañero de equipo las validará.'
            }
          </p>
        </div>

        {isValidationPending ? (
          <div style={{
            backgroundColor: '#2C2F33',
            borderRadius: isMobile ? '12px' : '8px',
            padding: isMobile ? '20px' : '16px',
            marginBottom: '20px',
            border: '1px solid #FF9800',
            textAlign: 'center'
          }}>
            <div style={{ 
              fontSize: isMobile ? '56px' : '48px', 
              marginBottom: isMobile ? '12px' : '8px' 
            }}>⏳</div>
            <div style={{ 
              color: '#FFFFFF', 
              fontWeight: 'bold', 
              marginBottom: '4px',
              fontSize: isMobile ? '18px' : '16px'
            }}>
              Esperando Validación
            </div>
            <div style={{ 
              color: '#B0BEC5', 
              fontSize: isMobile ? '15px' : '14px',
              lineHeight: '1.4'
            }}>
              Tu reporte será revisado pronto
            </div>
          </div>
        ) : validationStatus === 'APPROVED' ? (
          <div style={{
            backgroundColor: '#2C2F33',
            borderRadius: isMobile ? '12px' : '8px',
            padding: isMobile ? '20px' : '16px',
            marginBottom: '20px',
            border: '1px solid #4CAF50',
            textAlign: 'center'
          }}>
            <div style={{ 
              fontSize: isMobile ? '56px' : '48px', 
              marginBottom: isMobile ? '12px' : '8px' 
            }}>✅</div>
            <div style={{ 
              color: '#FFFFFF', 
              fontWeight: 'bold', 
              marginBottom: '4px',
              fontSize: isMobile ? '18px' : '16px'
            }}>
              Reporte Validado
            </div>
            <div style={{ 
              color: '#B0BEC5', 
              fontSize: isMobile ? '15px' : '14px', 
              marginBottom: '12px',
              lineHeight: '1.4'
            }}>
              Tus estadísticas han sido confirmadas y están cargadas
            </div>
            <div style={{
              backgroundColor: '#4CAF50',
              borderRadius: isMobile ? '12px' : '8px',
              padding: isMobile ? '16px' : '12px',
              border: '1px solid #2E7D32'
            }}>
              <div style={{ 
                color: '#FFFFFF', 
                fontSize: isMobile ? '16px' : '14px', 
                fontWeight: '600', 
                marginBottom: isMobile ? '8px' : '4px' 
              }}>
                📊 Estadísticas Finales
              </div>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(3, 1fr)', 
                gap: isMobile ? '12px' : '8px',
                marginTop: isMobile ? '12px' : '8px'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    color: '#FFFFFF', 
                    fontSize: isMobile ? '13px' : '12px', 
                    marginBottom: isMobile ? '4px' : '2px',
                    lineHeight: '1.2'
                  }}>⚽ Goles</div>
                  <div style={{ 
                    color: '#FFFFFF', 
                    fontSize: isMobile ? '22px' : '18px', 
                    fontWeight: 'bold' 
                  }}>{existingStats.goals}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    color: '#FFFFFF', 
                    fontSize: isMobile ? '13px' : '12px', 
                    marginBottom: isMobile ? '4px' : '2px',
                    lineHeight: '1.2'
                  }}>➡️⚽ Asistencias</div>
                  <div style={{ 
                    color: '#FFFFFF', 
                    fontSize: isMobile ? '22px' : '18px', 
                    fontWeight: 'bold' 
                  }}>{existingStats.assists}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    color: '#FFFFFF', 
                    fontSize: isMobile ? '13px' : '12px', 
                    marginBottom: isMobile ? '4px' : '2px',
                    lineHeight: '1.2'
                  }}>🧤⚽ Atajadas</div>
                  <div style={{ 
                    color: '#FFFFFF', 
                    fontSize: isMobile ? '22px' : '18px', 
                    fontWeight: 'bold' 
                  }}>{existingStats.saves}</div>
                </div>
              </div>
            </div>
          </div>
        ) : rejectionInfo && !showReportForm ? (
          <div style={{
            backgroundColor: '#2C2F33',
            borderRadius: isMobile ? '12px' : '8px',
            padding: isMobile ? '20px' : '16px',
            marginBottom: '20px',
            border: '1px solid #F44336',
            textAlign: 'center'
          }}>
            <div style={{ 
              fontSize: isMobile ? '56px' : '48px', 
              marginBottom: isMobile ? '12px' : '8px' 
            }}>❌</div>
            <div style={{ 
              color: '#FFFFFF', 
              fontWeight: 'bold', 
              marginBottom: isMobile ? '12px' : '8px',
              fontSize: isMobile ? '18px' : '16px'
            }}>
              Reporte Rechazado
            </div>
            <div 
              ref={rejectionReasonRef}
              style={{ 
                backgroundColor: '#181A1B', 
                borderRadius: isMobile ? '12px' : '8px', 
                padding: isMobile ? '16px' : '12px', 
                marginBottom: isMobile ? '12px' : '8px',
                border: '1px solid #40444B'
              }}
            >
              <div style={{ 
                color: '#F44336', 
                fontSize: isMobile ? '15px' : '14px', 
                fontWeight: '600', 
                marginBottom: isMobile ? '6px' : '4px' 
              }}>
                Motivo del rechazo:
              </div>
              <div style={{ 
                color: '#FFFFFF', 
                fontSize: isMobile ? '15px' : '14px', 
                lineHeight: '1.4' 
              }}>
                {rejectionInfo.reason}
              </div>
            </div>
            <div style={{ 
              color: '#B0BEC5', 
              fontSize: isMobile ? '13px' : '12px',
              marginBottom: isMobile ? '16px' : '12px'
            }}>
              Rechazado por: {rejectionInfo.rejectedBy}
            </div>
            <button
              onClick={handleShowReportForm}
              style={{ 
                backgroundColor: '#4CAF50', 
                borderRadius: isMobile ? '12px' : '8px', 
                padding: isMobile ? '14px 20px' : '12px 16px', 
                marginTop: isMobile ? '16px' : '12px',
                border: '1px solid #2E7D32',
                color: '#FFFFFF',
                fontSize: isMobile ? '16px' : '14px',
                fontWeight: '600',
                cursor: 'pointer',
                width: '100%',
                transition: 'background-color 0.2s',
                minHeight: isMobile ? '48px' : 'auto'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#45a049';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#4CAF50';
              }}
            >
              ✅ Corregir y Reenviar Reporte
            </button>
          </div>
        ) : (
          <div style={{
            backgroundColor: '#2C2F33',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px',
            border: '1px solid #40444B'
          }}>
            {rejectionInfo && (
              <div style={{
                backgroundColor: '#F44336',
                borderRadius: isMobile ? '12px' : '8px',
                padding: isMobile ? '16px' : '12px',
                marginBottom: isMobile ? '20px' : '16px',
                border: '1px solid #D32F2F',
                textAlign: 'center'
              }}>
                <div style={{ 
                  color: '#FFFFFF', 
                  fontSize: isMobile ? '15px' : '14px', 
                  fontWeight: '600', 
                  marginBottom: isMobile ? '6px' : '4px' 
                }}>
                  ⚠️ Reporte Anterior Rechazado
                </div>
                <div style={{ 
                  color: '#FFFFFF', 
                  fontSize: isMobile ? '13px' : '12px', 
                  lineHeight: '1.4' 
                }}>
                  Motivo: {rejectionInfo.reason}
                </div>
              </div>
            )}
            
            <div style={{ marginBottom: '16px' }}>
              <label htmlFor="self-report-goals" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#FFFFFF',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: '600',
                marginBottom: '8px'
              }}>
                ⚽ Goles:
              </label>
              <input
                id="self-report-goals"
                type="number"
                min="0"
                value={goals}
                onChange={(e) => setGoals(Math.max(0, parseInt(e.target.value) || 0))}
                style={{
                  width: '100%',
                  padding: isMobile ? '10px' : '12px',
                  borderRadius: '8px',
                  border: '1px solid #40444B',
                  backgroundColor: '#181A1B',
                  color: '#FFFFFF',
                  fontSize: isMobile ? '14px' : '16px'
                }}
                disabled={isLoading}
                placeholder="0"
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label htmlFor="self-report-assists" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#FFFFFF',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: '600',
                marginBottom: '8px'
              }}>
                ➡️⚽ Asistencias:
              </label>
              <input
                id="self-report-assists"
                type="number"
                min="0"
                value={assists}
                onChange={(e) => setAssists(Math.max(0, parseInt(e.target.value) || 0))}
                style={{
                  width: '100%',
                  padding: isMobile ? '10px' : '12px',
                  borderRadius: '8px',
                  border: '1px solid #40444B',
                  backgroundColor: '#181A1B',
                  color: '#FFFFFF',
                  fontSize: isMobile ? '14px' : '16px'
                }}
                disabled={isLoading}
                placeholder="0"
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label htmlFor="self-report-saves" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#FFFFFF',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: '600',
                marginBottom: '8px'
              }}>
                🧤⚽ Atajadas:
              </label>
              <input
                id="self-report-saves"
                type="number"
                min="0"
                value={saves}
                onChange={(e) => setSaves(Math.max(0, parseInt(e.target.value) || 0))}
                style={{
                  width: '100%',
                  padding: isMobile ? '10px' : '12px',
                  borderRadius: '8px',
                  border: '1px solid #40444B',
                  backgroundColor: '#181A1B',
                  color: '#FFFFFF',
                  fontSize: isMobile ? '14px' : '16px'
                }}
                disabled={isLoading}
                placeholder="0"
              />
            </div>

            <div style={{
              backgroundColor: '#1B5E20',
              borderRadius: '8px',
              padding: '12px',
              marginTop: '16px',
              border: '1px solid #2E7D32'
            }}>
              <div style={{ color: '#FFFFFF', fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                ⚠️ Importante
              </div>
              <div style={{ color: '#B0BEC5', fontSize: '12px', lineHeight: '1.4' }}>
                Una vez que envíes tu reporte, un compañero de equipo lo validará. 
                Después de la validación, no podrás modificar estas estadísticas.
              </div>
            </div>
          </div>
        )}

        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            disabled={isLoading}
            style={{
              padding: isMobile ? '10px 16px' : '10px 20px',
              backgroundColor: '#40444B',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: '600',
              minHeight: isMobile ? '44px' : 'auto'
            }}
          >
            {isValidationPending ? 'Cerrar' : 'Cancelar'}
          </button>
          {!isValidationPending && validationStatus === 'PENDING' && (showReportForm || !rejectionInfo) && (
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              style={{
                padding: isMobile ? '10px 16px' : '10px 20px',
                backgroundColor: '#4CAF50',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: '600',
                minHeight: isMobile ? '44px' : 'auto',
                opacity: isLoading ? 0.6 : 1
              }}
            >
              {isLoading ? 'Enviando...' : (rejectionInfo ? 'Reenviar Reporte' : 'Enviar Reporte')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}; 